import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Badge } from '@mui/material';
import {
  Home as HomeIcon,
  Chat as ChatIcon,
  Event as EventIcon,
  AccountTree as BrowseIcon,
  Diversity3 as CommunityIcon,
  Poll as SurveyIcon,
  HowToVote as HowToVoteIcon,
  Newspaper as NewsletterIcon,
  Campaign as CampaignIcon,
  Folder as FilesIcon,
  Groups as GroupsIcon,
  Store as MarketplaceIcon,
  VolunteerActivism as FundraiserIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  FactCheck as ApprovalsIcon,
} from '@mui/icons-material';
import { useThreadsContext } from '../../contexts/ThreadsContext';
import { useTenant } from '../../contexts/TenantContext';
import { useOpenVotesCount } from '../../hooks/useOpenVotesCount';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { useMarketplaceStatus } from '../../hooks/useMarketplaceStatus';
import { useFundraiserStatus } from '../../hooks/useFundraiserStatus';
import { getNavAriaLabel } from '../../utils/accessibility';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  /**
   * Extra prefixes this item owns. Some screens live beneath another item's
   * path — the newsletter authoring screens are under /admin/newsletters but
   * belong to Newsletters, not Site Admin — so an item can claim them here.
   */
  matchPaths?: string[];
  badge?: number;
}

interface NavigationProps {
  variant?: 'sidebar' | 'mobile';
  onNavigate?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ variant = 'sidebar', onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { totalUnreadCount } = useThreadsContext();
  const { config } = useTenant();
  const votingEnabled = !!config.features?.voting;
  const openVotesCount = useOpenVotesCount(votingEnabled);
  const { isCognitoAdmin, hasAnyAdminRole } = useAdminAccess();
  const { isEnabled: marketplaceEnabled } = useMarketplaceStatus();
  const { isEnabled: fundraiserEnabled } = useFundraiserStatus();

  const navigationItems: NavigationItem[] = [
    {
      id: 'home',
      label: 'Home',
      icon: <HomeIcon />,
      path: '/home',
    },
    {
      id: 'chats',
      label: 'Chats',
      icon: <ChatIcon />,
      path: '/chats',
      badge: totalUnreadCount,
    },
    {
      id: 'events',
      label: 'Events',
      icon: <EventIcon />,
      path: '/events',
    },
    {
      id: 'surveys',
      label: 'Surveys',
      icon: <SurveyIcon />,
      path: '/surveys',
    },
    // Only show Voting if the feature is enabled for this community
    ...(votingEnabled
      ? [
          {
            id: 'voting',
            label: 'Voting',
            icon: <HowToVoteIcon />,
            path: '/voting',
            badge: openVotesCount,
          },
        ]
      : []),
    {
      id: 'newsletters',
      label: 'Newsletters',
      icon: <NewsletterIcon />,
      path: '/newsletters',
      matchPaths: ['/admin/newsletters'],
    },
    {
      id: 'announcements',
      label: 'Announcements',
      icon: <CampaignIcon />,
      path: '/announcements',
    },
    {
      id: 'files',
      label: 'Files',
      icon: <FilesIcon />,
      path: '/files',
    },
    {
      id: 'groups',
      label: 'Groups',
      icon: <GroupsIcon />,
      path: '/groups',
    },
    {
      id: 'browse',
      label: 'Browse',
      icon: <BrowseIcon />,
      path: '/browse',
    },
    {
      id: 'directory',
      label: 'Community',
      icon: <CommunityIcon />,
      path: '/directory',
    },
    // Only show Fundraisers if it's enabled
    ...(fundraiserEnabled
      ? [
          {
            id: 'fundraisers',
            label: 'Fundraisers',
            icon: <FundraiserIcon />,
            path: '/fundraisers',
          },
        ]
      : []),
    // Only show Marketplace if it's enabled
    ...(marketplaceEnabled
      ? [
          {
            id: 'marketplace',
            label: 'Marketplace',
            icon: <MarketplaceIcon />,
            path: '/marketplace',
          },
        ]
      : []),
    // Show Approvals + Site Admin for any admin role (Cognito admins, entity admins, event admins)
    ...(hasAnyAdminRole()
      ? [
          {
            id: 'approvals',
            label: 'Approvals',
            icon: <ApprovalsIcon />,
            path: '/admin/approvals',
          },
          {
            id: 'admin',
            label: 'Site Admin',
            icon: <AdminIcon />,
            path: '/admin',
          },
        ]
      : []),
    {
      id: 'settings',
      label: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onNavigate) {
      onNavigate();
    }
  };

  // Nav items nest (/admin vs /admin/newsletters), so testing each item
  // independently lights up every ancestor. Pick a single winner: the longest
  // claimed prefix that matches the URL.
  const activeId = (() => {
    const claimLength = (claim: string) =>
      location.pathname === claim || location.pathname.startsWith(`${claim}/`) ? claim.length : -1;

    let bestId = '';
    let bestLength = -1;
    for (const item of navigationItems) {
      for (const claim of [item.path, ...(item.matchPaths ?? [])]) {
        const length = claimLength(claim);
        if (length > bestLength) {
          bestLength = length;
          bestId = item.id;
        }
      }
    }
    return bestId;
  })();

  const isActive = (item: NavigationItem) => item.id === activeId;

  return (
    <List
      component="nav"
      aria-label="Main navigation"
      sx={{ width: '100%', px: variant === 'sidebar' ? 1 : 0 }}
    >
      {navigationItems.map(item => (
        <ListItem key={item.id} disablePadding sx={{ mb: variant === 'sidebar' ? 0.5 : 0 }}>
          <ListItemButton
            onClick={() => handleNavigation(item.path)}
            selected={isActive(item)}
            aria-label={getNavAriaLabel(item.label, isActive(item), item.badge)}
            aria-current={isActive(item) ? 'page' : undefined}
            sx={{
              borderRadius: variant === 'sidebar' ? 2 : 0,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                },
              },
              ...(variant === 'mobile' && {
                flexDirection: 'column',
                py: 1,
                minHeight: 64,
              }),
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive(item) ? 'inherit' : 'text.secondary',
                minWidth: variant === 'mobile' ? 'auto' : 40,
                ...(variant === 'mobile' && {
                  mb: 0.5,
                }),
              }}
            >
              {item.badge && item.badge > 0 ? (
                <Badge badgeContent={item.badge} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: variant === 'mobile' ? '0.75rem' : '0.875rem',
                fontWeight: isActive(item) ? 600 : 400,
              }}
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

export default Navigation;
